// src/pages/SearchResults.jsx
import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Scorecard from '../components/Scorecard';
import TranscriptWindow from '../components/TranscriptWindow';
import { useVideoPopup } from '../components/VideoPopupContext';
import { findRelevantSegment, markQueryMatches, markSpeakerLabels } from '../utils/searchRelevance';
import './SearchResults.css';

/* ─── Helper: Smart Snippet Generation ───
   Transcript chunks are 500-word windows, so their first characters usually
   belong to whoever spoke BEFORE the part that matched the query. Anchor the
   snippet on the relevant segment instead: the speaker turn of a person named
   in the query ("[2:22:28] President Koo: …") when there is one, else the
   densest cluster of query words, else the exact phrase, else the start. */
function getSmartSnippet(content, term, maxLength = 250) {
  if (!content) return '';
  const fallback = () =>
    content.slice(0, maxLength) + (content.length > maxLength ? '...' : '');
  if (!term || !term.trim()) return fallback();

  const segment = findRelevantSegment(content, term);

  if (segment && segment.type === 'speaker') {
    // Start AT the "[ts] Speaker:" marker so the quote opens with the person
    // the user asked about.
    const start = segment.index;
    const end = Math.min(content.length, start + maxLength);
    const prefix = start > 0 ? '...' : '';
    const suffix = end < content.length ? '...' : '';
    return prefix + content.slice(start, end) + suffix;
  }

  // Center on the keyword cluster / exact phrase.
  const lowerContent = content.toLowerCase();
  const phraseIndex = lowerContent.indexOf(term.toLowerCase());
  const index = segment ? segment.index : phraseIndex;
  if (index === -1 || index < 50) return fallback();

  const halfLength = Math.floor(maxLength / 2);
  let start = index - halfLength;
  let end = index + halfLength;

  if (start < 0) {
    start = 0;
    end = maxLength;
  }
  if (end > content.length) {
    end = content.length;
    start = Math.max(0, end - maxLength);
  }

  const prefix = start > 0 ? '...' : '';
  const suffix = end < content.length ? '...' : '';

  return prefix + content.slice(start, end) + suffix;
}

/* ─── Helper: Skeleton Loader ─── */
function SkeletonCard() {
  return (
    <div className="result-card skeleton-card">
      <div className="card-meta">
        <div className="skeleton skeleton-badge"></div>
        <div className="skeleton skeleton-date"></div>
      </div>
      <div className="card-body">
        <div className="skeleton skeleton-title"></div>
        <div className="skeleton skeleton-text"></div>
        <div className="skeleton skeleton-text short"></div>
      </div>
    </div>
  );
}

/* ─── Helper: Extract first timestamp from snippet/content ─── */
function extractFirstTimestamp(content) {
  if (!content) return null;
  // Match HH:MM:SS or MM:SS, optionally bracketed
  const m = String(content).match(/\[?(\d{1,2}:\d{2}(?::\d{2})?)\]?/);
  return m ? m[1] : null;
}

/* ─── Helper: Result Card ─── */
function ResultCard({
  item,
  path,
  preservedSearch,
  term,
  isWidget,
  parentInfo,
  useProxy,
  isExpanded,
  onToggleExpand,
}) {
  const { showPopup } = useVideoPopup();
  const navigate = useNavigate();
  const isTranscript = item.title?.startsWith('Transcript') || item.title?.includes('Mention of');
  const displayTitle = item.title;

  const dateObj = new Date(item.date + 'T00:00:00');
  const formattedDate = isNaN(dateObj)
    ? item.date
    : dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const meetingAnchor = `meeting-${item.date}`;
  const specificAnchor = item.item_index ? `${meetingAnchor}-item-${item.item_index}` : meetingAnchor;
  const targetUrl = `${path}${preservedSearch}#${specificAnchor}`;

  let rawContent = (item.content || item.summary || '').replace(/^(Outcome:|Decision:|Impact:)\s*/i, '');
  let snippet = getSmartSnippet(rawContent, term);

  const processSnippet = (text) => {
    if (!text) return { __html: '' };

    // 1) Speaker labels named in the query ("President Koo:") — must run
    //    before timestamps become spans, since the label regex keys off the
    //    raw "[H:MM:SS] Label:" shape.
    let processed = markSpeakerLabels(text, term, 'speaker-highlight');

    // 2) Query words + exact phrase (tag-safe: skips inserted HTML).
    processed = markQueryMatches(processed, term, 'search-highlight');

    // 3) Timestamps become clickable spans.
    const timestampRegex = /(\[?\d{1,2}:\d{2}(?::\d{2})?\]?)/g;
    processed = processed.replace(timestampRegex, (match) => {
      const cleanTime = match.replace(/[[\]]/g, '');
      return `<span class="timestamp-link search-timestamp" data-timestamp="${cleanTime}">${match}</span>`;
    });

    return { __html: processed };
  };

  const handleTimestampClick = (e) => {
    if (e.target.classList.contains('search-timestamp')) {
      e.preventDefault();
      e.stopPropagation();
      const ts = e.target.getAttribute('data-timestamp');
      if (ts && item.video_url) {
        showPopup(ts, item.video_url, e.target);
      }
    }
  };

  const handleCardClick = (e) => {
    if (e.target.classList.contains('search-timestamp')) return;

    // Transcript hits: toggle the inline transcript window instead of navigating away.
    if (isTranscript) {
      onToggleExpand(item);
      return;
    }
    navigate(targetUrl);
  };

  const handleJumpToSummary = () => {
    navigate(targetUrl);
  };

  // Anchor time for the transcript window: the chunk-level start_timestamp /
  // start_time columns drift from the real video clock (they're synthetic
  // fixed-interval offsets), so we prefer a [H:MM:SS] marker pulled from the
  // SNIPPET first — it now starts at the query-relevant speaker turn, so its
  // first marker points at the moment the user actually asked about — then
  // the title/content markers, and only then the column values.
  const anchorTime =
    extractFirstTimestamp(snippet) ||
    extractFirstTimestamp(item.title) ||
    extractFirstTimestamp(rawContent) ||
    item.start_timestamp ||
    item.start_time ||
    null;

  return (
    <div className={`result-card ${isTranscript ? 'transcript-hit' : 'summary-hit'} ${isExpanded ? 'is-expanded' : ''}`}>
      <div
        className="card-link"
        onClick={handleCardClick}
        role="link"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
      >
        <div className="card-meta">
          <div className="meta-left">
            <span className={`type-badge ${isTranscript ? 'badge-transcript' : item.type === 'vote' ? 'badge-vote' : 'badge-meeting'}`}>
              {isTranscript ? 'Deep Search: Transcript' : item.type === 'vote' ? 'Voting Record' : 'Meeting Summary'}
            </span>
          </div>
          <div className="meta-right">
            <span className="meta-date">{formattedDate}</span>
          </div>
        </div>
        <div className="card-body">
          <h3 className="card-title">{displayTitle}</h3>
          <p
            className={`card-snippet ${isTranscript ? 'is-quote' : ''}`}
            onClick={handleTimestampClick}
          >
            {isTranscript && <span className="quote-mark">“</span>}
            <span dangerouslySetInnerHTML={processSnippet(snippet)} />
            {isTranscript && <span className="quote-mark">”</span>}
          </p>
        </div>
        {item.voting_id && !isTranscript && (
          <div className="card-scorecard"><Scorecard votingId={item.voting_id} /></div>
        )}
        {isTranscript && (
          <div className="card-footer">
            <span className="footer-action">
              {isExpanded ? '▾ Hide transcript context' : '▸ View transcript context'}
            </span>
          </div>
        )}
      </div>

      {isTranscript && isExpanded && (
        <TranscriptWindow
          meetingId={item.meeting_id}
          meetingDate={formattedDate}
          meetingTitle={null}
          searchTerm={term}
          anchorTime={anchorTime}
          videoUrl={item.video_url}
          isWidget={isWidget}
          parentInfo={parentInfo}
          useProxy={useProxy}
          onJumpToSummary={handleJumpToSummary}
          onClose={() => onToggleExpand(item)}
        />
      )}
    </div>
  );
}

/* ─── Main Component ─── */
export default function SearchResults({
  results,
  isLoading,
  onLoadMore,
  hasMore,
  onClearSearch,
  isWidget = false,
  parentInfo = null,
  useProxy,
}) {
  const loc = useLocation();
  const path = loc.pathname;
  const params = new URLSearchParams(loc.search);
  params.set('tab', 'meetings');
  const preservedSearch = `?${params.toString()}`;

  const [expandedKey, setExpandedKey] = useState(null);

  const handleToggleExpand = useCallback((item) => {
    const key = `${item.meeting_id || ''}-${item.id || ''}-${item.start_time || ''}`;
    setExpandedKey((prev) => (prev === key ? null : key));
  }, []);

  const isInitialLoading = isLoading && (!results || !results.summaries || results.summaries.length === 0);
  const isLoadingMore = isLoading && results?.summaries?.length > 0;

  if (isInitialLoading) {
    return (
      <div className="search-results-container">
        <header className="results-header">
          <div className="header-info">
            <h2>Searching...</h2>
            <p className="results-subtitle">Scanning board archives</p>
          </div>
        </header>
        <div className="results-grid"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>
      </div>
    );
  }

  let { summaries = [], term = '' } = results || loc.state || {};
  summaries = summaries || [];

  if (!term || !term.trim()) {
    return (
      <div className="empty-state initial-state">
        <h3>Begin your search</h3>
        <p>Explore board meetings, transcripts, and voting records by typing a keyword above.</p>
      </div>
    );
  }

  if (!summaries.length) {
    return (
      <div className="empty-state">
        <div className="empty-state-content">
          <h3>No results found</h3>
          <p>We couldn't find anything matching <span className="search-term-highlight">"{term}"</span>.</p>
          <div className="empty-actions">
            <button className="secondary-btn" onClick={onClearSearch}>Clear search and browse all</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results-container">
      <header className="results-header">
        <div className="header-info">
          <h2>Top Matches</h2>
          <p className="results-subtitle">Found {summaries.length} items related to <strong>"{term}"</strong></p>
        </div>
      </header>
      <div className="results-grid">
        {summaries.map((item, index) => {
          const itemKey = `${item.meeting_id || ''}-${item.id || ''}-${item.start_time || ''}`;
          return (
            <ResultCard
              key={`${item.id || index}-${index}`}
              item={item}
              path={path}
              preservedSearch={preservedSearch}
              term={term}
              isWidget={isWidget}
              parentInfo={parentInfo}
              useProxy={useProxy}
              isExpanded={expandedKey === itemKey}
              onToggleExpand={handleToggleExpand}
            />
          );
        })}
      </div>
      {hasMore && (
        <div className="load-more-container">
          <button className="load-more-btn" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? <><span className="spinner-small"></span> Loading...</> : 'Load More Results'}
          </button>
        </div>
      )}
    </div>
  );
}
